/**
 * engagementBrief · reading what the customer told us (sales-engagement-capture, E1 / E3 / E7).
 *
 * The backend writes two things this file reads, both pinned in the plan's §3a contract:
 *
 * 1. **Case briefs in `lead.notes`.** Plain text, one brief per inbound enquiry, newest first,
 *    separated by a line of exactly eight box-drawing dashes (`────────`). Each brief opens with a
 *    header line `Customer enquiry · {Website chat | Email} · {date}`, then labelled lines
 *    (`Contact:`, `Service:`, `Subject:` …). On an unowned lead the backend puts new briefs ABOVE
 *    whatever was there, so one notes string can hold briefs AND a rep's own text. The rep's text
 *    has no header and is never presented as the customer's.
 * 2. **AUTO_EVENT activities** whose `metadata.engagementKind` marks them as a customer or system
 *    event rather than something a rep logged (helpers shared with activities, re-exported here).
 *
 * Pure functions only — the components render, this decides. Tested in `engagementBrief.test.ts`.
 */
export {
  engagementKindOf,
  engagementLabel,
  type EngagementKind,
} from '@/features/activities/engagementKind'

/** The marker every auto-written brief starts with. */
export const BRIEF_HEADER_PREFIX = 'Customer enquiry ·'

/** The separator line, exactly as the backend writes it (eight U+2500, optional surrounding space). */
const SEPARATOR_LINE = /^\s*─{8}\s*$/

export interface NoteBlock {
  /** The brief's header line, or null for a block that is not an auto-brief (a rep's note). */
  heading: string | null
  /** Everything under the heading (or the whole block when there is no heading), trimmed. */
  body: string
}

function trimBlankLines(lines: string[]): string[] {
  let start = 0
  let end = lines.length
  while (start < end && lines[start].trim() === '') start++
  while (end > start && lines[end - 1].trim() === '') end--
  return lines.slice(start, end)
}

function isBriefHeader(line: string): boolean {
  return line.trim().startsWith(BRIEF_HEADER_PREFIX)
}

/**
 * Split notes into blocks on the `────────` separator.
 *
 * Returns `null` when the notes hold no auto-brief at all — the caller then renders them exactly
 * as before, so a rep's plain note never changes shape. Empty blocks are dropped.
 */
export function parseNoteBlocks(notes: string | null | undefined): NoteBlock[] | null {
  if (!notes || !notes.trim()) return null
  const lines = notes.replace(/\r\n?/g, '\n').split('\n')
  const rawBlocks: string[][] = [[]]
  for (const line of lines) {
    if (SEPARATOR_LINE.test(line)) rawBlocks.push([])
    else rawBlocks[rawBlocks.length - 1].push(line)
  }
  const blocks: NoteBlock[] = []
  for (const raw of rawBlocks) {
    const trimmed = trimBlankLines(raw)
    if (trimmed.length === 0) continue
    if (isBriefHeader(trimmed[0])) {
      blocks.push({ heading: trimmed[0].trim(), body: trimBlankLines(trimmed.slice(1)).join('\n') })
    } else {
      blocks.push({ heading: null, body: trimmed.join('\n') })
    }
  }
  return blocks.some((b) => b.heading !== null) ? blocks : null
}

export interface SplitNotes {
  /** The customer's enquiry briefs, newest first. Empty when the notes hold none. */
  briefs: NoteBlock[]
  /**
   * Everything else — the rep's own text — joined back with blank lines, or null when there is
   * none. When the notes hold no brief this is the notes string untouched.
   */
  repNotes: string | null
}

/** Separate the customer's briefs from the rep's text, so rep text is never shown as customer text. */
export function splitNotes(notes: string | null | undefined): SplitNotes {
  const blocks = parseNoteBlocks(notes)
  if (!blocks) return { briefs: [], repNotes: notes && notes.trim() ? notes : null }
  const briefs = blocks.filter((b) => b.heading !== null)
  const rep = blocks.filter((b) => b.heading === null).map((b) => b.body)
  return { briefs, repNotes: rep.length > 0 ? rep.join('\n\n') : null }
}

/**
 * An engagement event's description without a leading `Customer enquiry · …` line — the row's
 * label and subject already say it.
 */
export function stripBriefHeader(description: string | null | undefined): string {
  if (!description) return ''
  const lines = description.replace(/\r\n?/g, '\n').split('\n')
  const firstIdx = lines.findIndex((l) => l.trim() !== '')
  if (firstIdx === -1 || !isBriefHeader(lines[firstIdx])) return description
  return trimBlankLines(lines.slice(firstIdx + 1)).join('\n')
}

/** Longest preview string; the row also truncates visually with an ellipsis. */
export const PREVIEW_MAX_CHARS = 140

function truncate(s: string, max: number): string {
  return s.length <= max ? s : `${s.slice(0, max - 1).trimEnd()}…`
}

function stripLabel(line: string, label: string): string {
  return line.trim().slice(label.length).trim()
}

/** A chat line spoken by the bot — "Infinity AI:", "Assistant:", "Bot:", "Sales Agent:". */
const BOT_LINE = /^[^:]{0,40}\b(AI|Assistant|Bot|Agent)\s*:/i
/** A chat line spoken by the customer. */
const CUSTOMER_LINE = /^(Customer|Visitor|User)\s*:\s*/i
/** A line that is only a greeting — "Dear Sir,", "Hi Team,", "Good day", "Hello all!". */
const GREETING_ONLY = /^(dear|hi|hello|hey|greetings|good\s+(morning|afternoon|evening|day))\b(\s+[\w.'/-]+){0,3}\s*[,.!]?$/i

function isGreetingOnly(line: string): boolean {
  return GREETING_ONLY.test(line.trim())
}

/**
 * One line answering "what does this customer want?" for a Lead Pool row (E7).
 *
 * Taken from the LATEST brief (the backend prepends, so the first brief block), in order:
 *   1. its `Service: … · origin → destination` line (label removed);
 *   2. its `Subject:` line (label removed);
 *   3. the customer's first chat message (`Customer:` line, speaker removed, not a greeting);
 *   4. its first line that is not the header, `Contact:`, `Conversation:`, a bot's line or a
 *      greeting.
 * Null when the notes hold no brief — a rep's own note is not a customer request.
 */
export function customerRequestPreview(
  notes: string | null | undefined,
  maxChars: number = PREVIEW_MAX_CHARS,
): string | null {
  const latest = splitNotes(notes).briefs[0]
  if (!latest) return null
  const lines = latest.body
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l !== '')
  const service = lines.find((l) => l.startsWith('Service:'))
  if (service && stripLabel(service, 'Service:')) return truncate(stripLabel(service, 'Service:'), maxChars)
  const subject = lines.find((l) => l.startsWith('Subject:'))
  if (subject && stripLabel(subject, 'Subject:')) return truncate(stripLabel(subject, 'Subject:'), maxChars)
  for (const l of lines) {
    if (!CUSTOMER_LINE.test(l)) continue
    const said = l.replace(CUSTOMER_LINE, '').trim()
    if (said && !isGreetingOnly(said)) return truncate(said, maxChars)
  }
  const first = lines.find(
    (l) =>
      !l.startsWith('Contact:') &&
      l !== 'Conversation:' &&
      !BOT_LINE.test(l) &&
      !CUSTOMER_LINE.test(l) &&
      !isGreetingOnly(l),
  )
  return first ? truncate(first, maxChars) : null
}

/** Lines shown before "Show more" on an engagement event's description (`line-clamp-6`). */
export const COLLAPSED_LINES = 6

/**
 * Whether a description is long enough to need the collapse toggle without measuring the DOM:
 * more hard lines than the clamp, or enough characters to wrap past it at the drawer's width.
 * The component also measures the rendered clamp; this is the estimate before layout (and in
 * tests, where nothing is laid out).
 */
export function exceedsCollapsedLines(
  text: string | null | undefined,
  maxLines: number = COLLAPSED_LINES,
  approxCharsPerLine = 80,
): boolean {
  if (!text) return false
  const visualLines = text
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .reduce((n, line) => n + Math.max(1, Math.ceil(line.length / approxCharsPerLine)), 0)
  return visualLines > maxLines
}
